import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2022 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { ClassUtils } from "abweb/utils/ClassUtils";
import { createDateTimeParseError, preParseValue, shouldFixupParseStringWhitespace } from "abweb/utils/date/internal/DateTimeParserHelper";
import { StringUtils } from "abweb/utils/StringUtils";

// Only detects datetimes at start of trimmed parse string
const DATETIME_FORMAT_GLOBAL = /^([M|d|y|\-|\.|\/]+), (HH|hh|H|h)/g;
// So if parse string has ' ' or ',' separator between the date and time parts, replace it with ', '
const DATETIME_WHITESPACE_REPLACE = /([\d|\-|\.|\/]+)(?: |,)(\d)/; // non-global on purpose!

export const NARROW_SPACE_GLOBAL = /\u202F/g;

/**
 * Supports a chain of preprocessors and parsers that support more 'forgiving' parsing of the parse string
 * then the original parser.  It always tries the primary parser with the original parse string first.
 * It supports registering secondary parse string preprocessors that will adjust the parse string
 * before it is fed into the primary parser for a second try at parsing.
 * In addition, it supports feeding the original parse string into registered secondary parsers.
 * The first parse attempt that successfully parses a parse string stops further processing.
 *
 * Note that secondaryPreProcessor usage is preferred over secondaryParsers, as the preprocessors are cumulative
 * (all preprocessors run before the resulting parse string is fed into the parsers) while the parsers are independently
 * invoked.  So if have a parse string that has two 'customizations', each handled by a different custom parser,
 * then it will fail as there is no <b>single</b> parser that will handle both customizations.
 */
export class DateTimeParserChain {
  constructor(primaryParser) {
    _defineProperty(this, "secondaryParsers", []);
    _defineProperty(this, "parseStringPreprocessors", []);
    this.primaryParser = primaryParser;
    this.fixupWhitespace = shouldFixupParseStringWhitespace(primaryParser.parseContext.parseFormat);
  }
  get parseContext() {
    return this.primaryParser.parseContext;
  }
  addSecondaryParser(secondaryParser) {
    this.secondaryParsers.push(secondaryParser);
  }
  addParseStringPreprocessor(preProcessor) {
    this.parseStringPreprocessors.push(preProcessor);
  }
  preprocessParseStr(parseStr) {
    // All of the preprocessors perform search-and-replace in the parseStr, so normalize whitespace first
    // when possible so that downstream preprocessors can deal with 'normalized whitespace' (more reliable search-and-replace)
    let fixedVal = preParseValue(parseStr, this.fixupWhitespace);
    if (fixedVal) {
      this.parseStringPreprocessors.forEach(pp => fixedVal = pp(fixedVal));
    }
    return fixedVal ?? undefined;
  }
  parse(val) {
    // First try primary parser with raw user input string: strict parsing
    let primaryRetVal = this.primaryParser.parse(val);
    if (ClassUtils.isInstanceOf(primaryRetVal, Date)) return primaryRetVal;

    // Need 'forgiveness' so add in preprocessors to adjust input 'val' and run it against the parser(s).
    const fixedVal = this.preprocessParseStr(val);
    if (!fixedVal) {
      return createDateTimeParseError(val, this.primaryParser.parseContext);
    }
    primaryRetVal = this.primaryParser.parse(fixedVal);
    if (ClassUtils.isInstanceOf(primaryRetVal, Date)) return primaryRetVal;
    for (const secondaryParser of this.secondaryParsers) {
      // Do NOT pass in fixedVal to secondary parser.  The secondary parser has a different parse format string,
      // so it may have different 'triggered' preprocessors than the primary parser.  It is up to secondary parser
      // to implement its own pre-processing (if any).
      const secondaryRetVal = secondaryParser.parse(val);
      if (ClassUtils.isInstanceOf(secondaryRetVal, Date)) return secondaryRetVal;
    }
    // Return primary error if no successful parses.
    // Note error message will have 'fixedVal' as parse string, which is helpful for debugging
    return primaryRetVal;
  }

  /** If no secondary preprocessors/parsers registered, this just returns the primary parser for efficiency */
  dateTimeParserFactory() {
    // Ignore the whitespace normalization, as primary parser is responsible for its own whitespace normalization
    return this.secondaryParsers.length > 0 || this.parseStringPreprocessors.length >= 0 ? this : this.primaryParser;
  }
}

///////   Optional Parse Preprocessors and 'Secondary' Parsers below ////////

/**
 * When a date separator is used (e.g., the '/' in 11/16/1969, the '-' in 11.16.1969, the '.' in 11.16.1969) and that
 * is the only time it is used in the format string, then allow any of the three known separator characters [/\-\.].
 * @param dtpChain
 */
export function dateSeparatorPreprocessor(dtpChain) {
  // parseFormat:   "MM/dd/yyyy   'but I foo-bar and foo.bar'"
  const parseFormat = dtpChain.parseContext.parseFormat;
  const separators = "/-.";
  let dateSeparator;
  for (let i = 0; i < separators.length; i++) {
    const sep = separators[i];
    const numMatches = StringUtils.countSubstrings(parseFormat, sep);
    if (numMatches > 0) {
      if (dateSeparator // don't support global search and replace if two different date separators found in parseFormat
      || numMatches !== numDateSeparators(parseFormat, sep))
        // all usages of sep must be within date formats
        {
          return;
        }
      dateSeparator = sep;
    }
  }
  if (!dateSeparator) return;
  const otherSeps = separators.replace(dateSeparator, "");
  const otherSeparatorsRegEx = new RegExp(`[${StringUtils.escapeRegExp(otherSeps)}]`, "g");
  dtpChain.addParseStringPreprocessor(parseStr => {
    // Normalized other separators to the dateSeparator expected by the parse string
    return parseStr.replace(otherSeparatorsRegEx, dateSeparator);
  });
}
function numDateSeparators(parseFormat, dateSeparator) {
  const escapedSep = StringUtils.escapeRegExp(dateSeparator);
  const ymd = "(y|M|d)"; // "(y|M|d)";  // luxon or kendo
  // ?<= is positive lookbehind, ?= is positive lookahead
  const regEx = new RegExp(`(?<=${ymd})${escapedSep}(?=${ymd})`, "g");
  const results = parseFormat.match(regEx);
  return results?.length ?? 0;
}

/**
 * When format has ', ' as a datetime separator (e.g., 1/2/2003, 12:34), then make the comma optional for parsing
 * @param dtpChain
 */
export function commaDateTimeSeparatorPreprocessor(dtpChain) {
  // Basic idea: if parse format has first two parts being date (m|d|y) and time (hh|h) with delimiter being ', ',
  // then at runtime parse string replace whitepsace delimiter ([, ]+) with ', '
  // Note: initially anchored off of '\d:' to denote start of time string,
  // but that did not work if they entered shorthand '1 pm'
  const luxonParseFormat = dtpChain.parseContext.parseFormat;
  if (StringUtils.countSubstrings(luxonParseFormat, DATETIME_FORMAT_GLOBAL) !== 1) return; // returns 0 or 1 due to '^'

  dtpChain.addParseStringPreprocessor(parseStr => {
    // Use first whitespace ("[ ,]+") as separator between date and time portions, and replace whitespace with ', '
    parseStr = parseStr.replace(DATETIME_WHITESPACE_REPLACE, "$1, $2");
    return parseStr;
  });
}

/**
 * When parse string has narrow spaces in it (probably from cut-n-paste as hard to enter via keyboard), convert to normal space
 * @param dtpChain
 */
export function narrowToNormalSpacePreprocessor(dtpChain) {
  dtpChain.addParseStringPreprocessor(parseStr => {
    parseStr = parseStr.replace(NARROW_SPACE_GLOBAL, " ");
    return parseStr;
  });
}

// Implement directly in this module so that don't get import cycles between this and DateTimeParserChain (where I'd like to put it...)
/**
 * Luxon-specific:
 * Adds a " " before the am/pm if missing (3:00pm becomes 3:00 PM).
 * Also support using first char of localized am/pm (case-insensitive) instead of the full localized value
 * as long the first char is different between the localized am and pm values
 */
export function amPmParseStringPreprocessor(dtpChain, localizedAM, localizedPM) {
  const luxonParseFormat = dtpChain.parseContext.parseFormat;
  if (!luxonParseFormat.endsWith(" a")) {
    return undefined;
  }
  // Regex are expensive to create, so do it at 'compile time' instead of within preprocessor

  // matches parse strings that do not have a space fore the ending AM or PM
  const spaceBeforeAmPmRE = new RegExp(`(?<! )(${localizedAM}|${localizedPM})$`, "i");
  let validAmPmRE, endsWithARE, endsWithPRE;
  const supportFirstCharAmPm = localizedAM[0] !== localizedPM[0];
  if (supportFirstCharAmPm) {
    validAmPmRE = new RegExp(`(${localizedAM}|${localizedPM})$`, "i");
    endsWithARE = new RegExp(`${localizedAM[0]}$`, "i");
    endsWithPRE = new RegExp(`${localizedPM[0]}$`, "i");
  }
  function convertFirstCharToAmPm(parseStr) {
    if (parseStr.match(validAmPmRE)) return parseStr; // already ends with AM or PM (case-insensitive)

    // ensure only one of the two fires (otherwise if localizedAM ends with first char of localizedPm would both fire)
    let retVal = parseStr.replace(endsWithARE, localizedAM);
    if (retVal === parseStr) {
      retVal = parseStr.replace(endsWithPRE, localizedPM);
    }
    return retVal;
  }
  function addSpaceBeforeEndStr(parseStr) {
    return parseStr.replace(spaceBeforeAmPmRE, " $1");
  }
  dtpChain.addParseStringPreprocessor(parseStr => {
    parseStr = supportFirstCharAmPm ? convertFirstCharToAmPm(parseStr) : parseStr;
    parseStr = addSpaceBeforeEndStr(parseStr);
    return parseStr;
  });
}
//# sourceMappingURL=DateTimeParserChain.js.map
